/* eslint-disable @typescript-eslint/no-explicit-any */

import { number, object, string } from "yup";

import { ProductFormData, SkuFormData } from "@/components/screens/ProductFormPage/types";
import { ProductVariation } from "@/interfaces/product.interface";
import { IProductImage } from "@/interfaces/product-image.interface";
import { ISku, ISkuInput, IVariation } from "@/interfaces/sku.interface";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";
import { SkuFormMessages } from "@/messages/sku/sku-form.messages";
import { ToastProps } from "@/types/toast.types";

import { DIVISOR_FACTOR } from "./constants";

export const buildSkuFormPageInitialData = (
  sku: ISku | null,
  product: ProductFormData,
  savedImages?: IProductImage[]
): SkuFormData => {
  let validImageAddress = sku?.imagePhisicalAddress || "";

  if (validImageAddress && savedImages && savedImages.length > 0) {
    const imageExists = savedImages.some((image) => image.phisicalAddress === validImageAddress);

    if (!imageExists) {
      validImageAddress = "";
    }
  }

  const normalizedVariations = (() => {
    if (!sku?.variations) {
      return "";
    }

    if (typeof sku.variations === "string") {
      return sku.variations;
    }

    return JSON.stringify(sku.variations);
  })();

  return {
    id: sku?.id || 0,
    active: sku?.active !== undefined ? sku.active : true,
    customerId: sku?.customerId || 0,
    productId: sku?.productId || 0,
    productCode: sku?.productCode ? sku.productCode : "",
    code: sku?.code || "",
    title: sku?.title || product.title,
    width: sku?.width || 0,
    height: sku?.height || 0,
    weight: sku?.weight || 0,
    depth: sku?.depth || 0,
    dimensionalWeight: sku?.dimensionalWeight || 0,
    ean: sku?.ean || "",
    kit: !!sku?.kit,
    stock: sku?.stock || 0,
    reservedStock: sku?.reservedStock || 0,
    leadTime: sku?.leadTime || 0,
    mainPrice: (sku?.mainPrice || 0).toString(),
    promoPrice: (sku?.promoPrice || 0).toString(),
    startPromoPrice: sku?.startPromoPrice || "",
    endPromoPrice: sku?.endPromoPrice || "",
    kitComponents: sku?.kitComponents || [],
    stockMovement: sku?.stockMovement || undefined,
    videoUrl: sku?.videoUrl || "",
    imageUrl: sku?.imageUrl || "",
    imagePhisicalAddress: validImageAddress,
    variations: normalizedVariations,
    rowDate: sku?.rowDate || new Date(),
    kitsToDelete: [],
  };
};

export const buildSkuDataFormSchema = (messages: SkuFormMessages, isVariationProduct: boolean) =>
  object().shape({
    title: isVariationProduct ? string().required(messages.errorRequiredTitle) : string(),
    code: string().required(messages.errorRequiredCode),
    mainPrice: number()
      .required(messages.errorRequiredMainPrice)
      .min(0.01, messages.errorMainPriceGreaterThanZero),

    startPromoPrice: string()
      .nullable()
      .test(
        "is-before-endPromoPrice",
        messages.errorStartPromoPriceMustBeBeforeEndPromoPrice,
        function (value) {
          const { endPromoPrice } = this.parent;
          return (
            !value ||
            !endPromoPrice ||
            new Date(value).getTime() <= new Date(endPromoPrice).getTime()
          );
        }
      )
      .when("promoPrice", (promoPrice, schema) =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        promoPrice && promoPrice > 0
          ? schema.required(messages.errorRequiredPromotionStartDate)
          : schema.nullable()
      ),
    endPromoPrice: string()
      .nullable()
      .test(
        "is-after-startPromoPrice",
        messages.errorEndPromoPriceMustBeAfterStartPromoPrice,
        function (value) {
          const { startPromoPrice } = this.parent;
          return (
            !value ||
            !startPromoPrice ||
            new Date(value).getTime() >= new Date(startPromoPrice).getTime()
          );
        }
      )
      .when("promoPrice", (promoPrice, schema) =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        promoPrice > 0 ? schema.required(messages.errorRequiredPromotionEndDate) : schema
      ),

    weight: string()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .test("weight-format", messages.errorWeightPrecision, (value) => {
        if (value === null || value === undefined || value === "") return true;

        const raw = value.toString().replace(",", ".");
        const numeric = Number(raw);

        if (Number.isNaN(numeric) || numeric < 0) return false;

        const formatted = numeric.toFixed(3);
        return /^\d+(\.\d{3})$/.test(formatted);
      }),

    height: number()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .min(0),

    width: number()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .min(0),

    depth: number()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .min(0),

    leadTime: number()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .min(0)
      .integer(messages.errorLeadTimeInteger),
  });

export function calculateDimensionalWeight(
  height?: number,
  width?: number,
  depth?: number
): number {
  if (!height || !width || !depth) {
    return 0;
  }
  return (height * width * depth) / DIVISOR_FACTOR;
}

export async function checkPromoPriceConditions(
  formData: ISkuInput,
  messages: ProductFormPageMessages,
  showToast: (toast: ToastProps) => void
) {
  const { errorRequiredPromotionDates, errorPromoPriceGreaterThanZero } = messages;

  const promoPrice = formData.promoPrice ? Number(formData.promoPrice) : 0;
  if (promoPrice > 0 && (!formData.startPromoPrice || !formData.endPromoPrice)) {
    showToast({ text: errorRequiredPromotionDates, type: "error" });
    return true;
  } else if ((formData.startPromoPrice || formData.endPromoPrice) && promoPrice <= 0) {
    showToast({ text: errorPromoPriceGreaterThanZero, type: "error" });
    return true;
  }
  return false;
}

export function checkVariations(
  variations: string,
  messages: ProductFormPageMessages,
  showToast: (toast: ToastProps) => void
) {
  const { errorAllVariationsMustHaveAValue } = messages;

  let parsedVariations = [];

  // Permitir variações vazias - isso permite adicionar variações posteriormente
  if (!variations || variations.trim() === "") {
    return false; // Sem erro - permite salvar
  }

  try {
    parsedVariations = JSON.parse(variations);
  } catch (error) {
    showToast({ text: "Erro ao analisar as variações.", type: "error" });
    return true;
  }

  // Se existem variações definidas, todas devem ter valor
  const incompleteVariation = parsedVariations.some((v: IVariation) => !v.VariationValue);

  if (incompleteVariation) {
    showToast({ text: errorAllVariationsMustHaveAValue, type: "error" });
    return true;
  }
  return false;
}

export function mapSkuVariations(variations: IVariation[] | undefined): UnifiedVariation[] {
  if (!variations || variations.length === 0) {
    return [];
  }

  return variations
    .filter((v) => v && v.VariationCode !== undefined && v.VariationName)
    .map(({ VariationCode, VariationName, VariationValue }) => ({
      code: VariationCode.toString(),
      name: VariationName,
      value: VariationValue || "",
    }));
}

export interface UnifiedVariation {
  code: string;
  name: string;
  value?: string;
}

export function mapProductVariations(
  variations: ProductVariation[] | undefined
): UnifiedVariation[] {
  return (variations ?? []).map(({ code, name }) => ({
    code: code.toString(),
    name,
    value: "",
  }));
}



